import React from 'react';
import { Button, Icon } from '@design-manager-inc/ui';
import type { IMoodBoard, TMoodBoardStatuses } from 'api/resources/moodBoards';
import {
	isMoodBoardStatusApproved,
	isMoodBoardStatusRejected,
} from 'api/resources/moodBoards';
import { usePatchMoodBoard } from 'api/hooks/usePatchMoodBoard';
import { track } from 'app/features/Analytics/AnalyticsUtils';
import {
	PAGES,
	TRACK_ACTION_TYPES,
} from 'app/features/Analytics/utils/constants';

interface IMoodBoardButtonsProps {
	moodBoard: IMoodBoard;
	// The client's access token, which addresses the update endpoint.
	token: string;
}

// Reject / Approve pair, styled after the proposal card's action buttons.
// Both are always offered; the one matching the board's current status is
// filled in, the other is outlined, so the pair doubles as the status readout
// and lets the client change their mind. A failed save is reported by the
// global mutation toast, like every other mutation in the portal.
export const MoodBoardButtons = ({
	moodBoard,
	token,
}: IMoodBoardButtonsProps) => {
	const { mutate: patchMoodBoard, isPending } = usePatchMoodBoard(
		token,
		moodBoard.id,
	);

	const isApproved = isMoodBoardStatusApproved(moodBoard.approvalStatus);
	const isRejected = isMoodBoardStatusRejected(moodBoard.approvalStatus);

	const setStatus = (status: TMoodBoardStatuses, event: string) => {
		if (status === moodBoard.approvalStatus) {
			return;
		}

		patchMoodBoard(
			{ approvalStatus: status },
			{
				onSettled: () => {
					track(event, {
						'Action Type': TRACK_ACTION_TYPES.SAVE,
						'Screen Name': `${PAGES.MOODBOARDS} Page`,
					});
				},
			},
		);
	};

	return (
		<div className="w-full flex flex-col items-center gap-2 md:flex-row lg:justify-end">
			<Button
				variant={isRejected ? 'black' : 'white'}
				radius="full"
				className="w-full rounded-full"
				size="sm"
				disabled={isPending}
				aria-pressed={isRejected}
				onClick={() => setStatus('rejected', 'Mood Board Rejected')}
				data-testid="mood-board-reject"
			>
				<span
					className={`whitespace-nowrap flex items-center ${isRejected ? 'text-white' : ''}`}
				>
					<Icon
						name="x-circle"
						className={`mr-2 ${isRejected ? 'fill-white' : 'fill-[--secondary-color]'}`}
					/>
					<span className="text-md md:text-sm lg:text-xs">Reject</span>
				</span>
			</Button>
			<Button
				variant={isApproved ? 'primary' : 'white'}
				radius="full"
				className="w-full rounded-full"
				size="sm"
				disabled={isPending}
				aria-pressed={isApproved}
				onClick={() => setStatus('approved', 'Mood Board Approved')}
				data-testid="mood-board-approve"
			>
				<span
					className={`whitespace-nowrap mx-3 flex items-center ${isApproved ? 'text-white' : ''}`}
				>
					<Icon
						name="check-circle"
						className={`mr-2 ${isApproved ? 'fill-white' : 'fill-[--secondary-color]'}`}
					/>
					<span className="text-md md:text-sm lg:text-xs">Approve</span>
				</span>
			</Button>
		</div>
	);
};

MoodBoardButtons.displayName = 'MoodBoardButtons';
